import { useState, useEffect } from "react";
import {
  CheckCircle, Circle, ChevronDown, ChevronRight, X, MessageSquare, ExternalLink, Award, Palette,
  MessageCircle, BookOpen, Pencil, Scroll, Lock, Globe, Users, Settings,
  BarChart2, LogOut, RotateCcw, AlertCircle, CircleUserRound, Sparkles, PenLine,
  Building2, BookUser,
} from "lucide-react";
import {
  IDEATION_MODULES, ACCELERATOR_MODULES, MOMENTUM_MODULES,
  MODULE_PROMPTS, MODULE_META, BLUEPRINT_LABELS,
} from "../constants/modules";
import { PHASE_TONES } from "../constants/colors";
import { detectCompletedPrompts, parseOutputs, getVisiblePrompts, sectionProgress } from "../utils/messages";
import { ProgressBar } from "./ProgressBar";
import { BUCKETS, isAdminEmail, toolStatus } from "./MomentumHub";
import { builtToolIds } from "../utils/momentumBuilt";
import BrillLogo from "./BrillLogo";
import { ME_SCREENS } from "./MeFrame";
import { AskBrillMenu, Menu2Header } from "./AskBrillMenu";

/**
 * The sidebar is CHROME: a fixed palette in both panel modes. Nothing in this
 * file may read a canvas token or branch on panel mode — if it did, the "Dark
 * panel" toggle would stop meaning "the working area" and start meaning "the
 * whole app", which is the distinction the two-scope system exists to draw.
 *
 * Structure, top to bottom:
 *   brand header · global destinations · phase switcher · phase tree · footer
 *
 * Only ONE phase's tree is mounted at a time. That is what lets three phases
 * share a sidebar without it running off the bottom of the screen. Ask Brill and
 * Me (the Archetype items, "Archetype Assessment" until 2026-09-17) sit ABOVE
 * the switcher because they belong to no phase and
 * must stay reachable whichever phase is selected.
 *
 * Font sizes follow the floor from d81253b: text 11px, enclosed badges 10px.
 */

const PHASES = [
  { key: "ideation",    label: "Ideation",    tone: PHASE_TONES.ideation },
  { key: "accelerator", label: "Accelerator", tone: PHASE_TONES.accelerator },
  { key: "momentum",    label: "Momentum",    tone: PHASE_TONES.momentum },
];

/* The bucket lines in the Momentum tree, and the ramp above them.

   `BUCKETS[].color` is always a 6-digit brand hex (a canvas token would resolve
   wrong on the always-dark chrome — see the note beside BUCKETS), so the alpha
   can just be appended as a 7th/8th digit. If one of those ever becomes a
   `var(...)`, this concatenation silently produces an invalid colour and the
   line disappears — swap to color-mix() at that point.

   Fading in rather than sitting at full strength keeps four coloured lines from
   shouting over the tree, and gives each one a direction.                      */
const bucketLine = (hex) => `linear-gradient(90deg, ${hex}1F 0%, ${hex}CC 100%)`;

/* The same four colours as one continuous ramp, standing in for the phase
   progress bar under "Momentum". Build's bright teal through to Run's deep one. */
const PHASE_RAMP = `linear-gradient(90deg, ${BUCKETS.map((bk) => bk.color).join(", ")})`;

/* A Momentum tool's nav id. Some carry an explicit route; the rest are namespaced
   so they cannot collide with a module id. Mirrors the tool row's onSelect. */
const toolNavId = (it) => it.route || `m/${it.id}`;

const MOMENTUM_NAV_IDS = new Set([
  ...BUCKETS.flatMap((bk) => bk.items.map(toolNavId)),
  ...MOMENTUM_MODULES,
]);

/**
 * Which phase owns a destination, or null for the global ones (Ask Brill, the
 * archetype items, settings, admin). Returning null rather than defaulting to a
 * phase is deliberate — the design review found that falling through to a phase
 * made Ask Brill claim to be inside Momentum.
 */
function phaseOf(id) {
  if (IDEATION_MODULES.includes(id)) return "ideation";
  if (ACCELERATOR_MODULES.includes(id)) return "accelerator";
  if (MOMENTUM_NAV_IDS.has(id)) return "momentum";
  return null;
}

/* Sidebar width is set by the widest switcher tab: "ACCELERATOR" has to fit
   three-up without truncating. 240px before the switcher existed, 282px once it
   did, and 300px now that a locked tab also carries a padlock. The switcher's
   own padding below is tightened for the same reason — the label is the
   constraint, so everything around it gives way first. */
const SIDEBAR_WIDTH = 300;

/* The small icon buttons revealed on a completed sub-step's hover. */
const HOVER_ACTION = {
  background: "var(--chrome-hover)", border: "none", borderRadius: 4,
  padding: "3px 5px", cursor: "pointer", display: "flex", alignItems: "center",
};

/* A module row the user hasn't bought access to. Uses a dim colour rather than
   opacity — opacity drops contrast on every layer at once, including the icon. */
const LOCKED_ROW = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "8px 10px", borderRadius: 8, marginBottom: 1, cursor: "default",
};

/* One Momentum group: a single shaded container wrapping its header and tools. */
const GROUP_CARD = {
  borderRadius: 10, background: "var(--chrome-quiet)",
  border: "1px solid var(--chrome-border)", overflow: "hidden", marginBottom: 6,
};

/* A SECOND-LEVEL MENU's colours (colors.js `menu2-*`), handed to everything
   under it as the chrome tokens it already reads. */
const MENU2 = {
  background: "var(--menu2-bg)",
  "--chrome-bg": "var(--menu2-bg)",
  "--chrome-text": "var(--menu2-text)",
  "--chrome-text-secondary": "var(--menu2-text-secondary)",
  "--chrome-muted": "var(--menu2-text-secondary)",
  "--chrome-disabled": "var(--menu2-text-secondary)",
  "--chrome-accent": "var(--menu2-accent)",
  "--chrome-border": "var(--menu2-border)",
  "--chrome-quiet": "var(--menu2-quiet)",
  "--chrome-hover": "var(--menu2-hover)",
};

/* Me's level: its open row is WHITE, not the teal fill the main menu uses
   (her rule for a second level). Ask Brill's rows read --menu2-row-on direct. */
const MENU2_ME = {
  ...MENU2,
  "--chrome-accent-solid": "var(--menu2-row-on)",
  "--chrome-on-accent-solid": "var(--menu2-text)",
};

/** Which menu level belongs to a destination: the chats, Me, or the main menu. */
export function menuLevelFor(where, askMenuOn) {
  if (where === "chat" && askMenuOn) return "ask";
  if (ME_SCREENS.includes(where) || where === "archetype_results" || where === "archetype_feedback") return "me";
  return "main";
}

export function Sidebar({ active, onSelect, moduleMessages, blueprints, isOpen, onClose, isMobile, onViewOutput, onViewBlueprint, onStartPrompt, onReviseSection, user, onSignOut, onResetModule, onResetAll, onViewMasterBlueprint, onViewIdeationBlueprint, hasAcceleratorAccess, hasMomentumAccess, phaseRequest, askChats = null, onNewAskChat, onOpenAskChat, onRenameAskChat, onDeleteAskChat, activeNiche, nicheStaleCount = 0, nicheStaleByModule = {}, nicheStaleTierByModule = {}, onOpenNicheTriage, onOpenNiche, offers = [], momentumCounts = {}, moduleOutputs = {}, outputStamps = {}, outputsLoaded = false }) {
  /* DONE MEANS STORED. STALE IS A SEPARATE THING, SAID SEPARATELY.
     Every tick in this sidebar used to come from `detectCompletedPrompts`, which
     searches the transcript for `✓ LOCKED:`. A transcript cannot be un-said, so
     a section Brill only claimed to lock stayed ticked while being absent from
     the database. That moved onto storage on 2026-08-25 and fixed the lie.

     It then over-corrected, and a live client hit it on 2026-09-01. "Stored AND
     current" meant that the moment she changed niche, every section she had
     completed lost its tick, the module read 0%, and the row became
     indistinguishable from one she had never started -- clicking it started the
     prompt over rather than opening work that was in the database.

     So the tick answers "has she built this?" and the SALMON answers "does it
     still fit the niche she is on?". Her call: "she did complete them so they
     should be checked and count toward her total." Both sets come out of one
     pass in `sectionProgress` so they can never disagree.

     Falls back to the old transcript reading until her outputs arrive, or the
     whole sidebar would empty itself for a beat on every load. */
  const progressIn = (id) => {
    const msgs = moduleMessages[id] || [];
    if (!outputsLoaded) return { built: detectCompletedPrompts(id, msgs), past: new Set() };
    return sectionProgress({
      prompts: getVisiblePrompts(id, msgs),
      savedOutputs: moduleOutputs[id],
      stamps: outputStamps[id],
      activeNicheId: activeNiche?.id || null,
    });
  };
  const completedIn = (id) => progressIn(id).built;

  /* ── The text behind a ticked row ────────────────────────────────────────
   *
   * THE BUG (2026-08-30). `completedIn` above was moved onto storage, but the
   * CONTENT beside it was still read out of the transcript with `parseOutputs`
   * — in two places, which is how they drifted apart without anyone noticing.
   * One row, two sources, and every symptom came from the disagreement:
   *
   *   - A section hand-edited in Supabase, refiled from another module, or
   *     whose chat was trimmed by history caching, has a row but no block in
   *     any transcript. Green tick, and then no Copy and no Revise on hover,
   *     because those are gated on finding the text. Clicking it dropped her
   *     into the conversation instead of opening her own work. A live client's
   *     combined Method is exactly this — it was written to the database.
   *   - Worse, where the stored version and the transcript version differ,
   *     Copy handed her the OLDER text. Nothing on screen said so.
   *
   * A row IS the lock, so the row is also the text. The transcript survives
   * only until her outputs land, matching `completedIn` — and once they have,
   * this stops parsing the transcript at all, which is strictly less work per
   * module render than before.
   *
   * ONE HELPER, not two call sites, so the tick and the text cannot disagree
   * again the next time one of them is edited. */
  const outputsIn = (id) => (
    outputsLoaded
      ? (moduleOutputs[id] || {})
      : parseOutputs(moduleMessages[id] || [])
  );

  const moduleComplete = (id) => {
    const msgs = moduleMessages[id] || [];
    const visiblePrompts = getVisiblePrompts(id, msgs);
    if (visiblePrompts.length === 0) return false;
    return completedIn(id).size === visiblePrompts.length;
  };

  const ideationDoneCount = IDEATION_MODULES.filter((id) => {
    const prompts = MODULE_PROMPTS[id] || [];
    if (prompts.length === 0) return false;
    const completed = completedIn(id);
    return prompts.every((p) => completed.has(p));
  }).length;

  const acceleratorDoneCount = ACCELERATOR_MODULES.filter(moduleComplete).length;
  const allAcceleratorComplete = acceleratorDoneCount === ACCELERATOR_MODULES.length;

  /**
   * Which Momentum tools have something saved.
   *
   * ONE HELPER, so the tick beside a tool and the bar above it can never
   * disagree — `builtToolIds` in utils/momentumBuilt.js is the only answer, and
   * both readers below call it once.
   *
   * The comment that used to sit here said the other tools' tables "have no code
   * reading them yet". True in July, false since: Sites, Courses, Outreach,
   * Meeting Links and the Social Generator all read and write their own tables,
   * so the bar reported the same for a coach with twelve modules written as for
   * one who had written nothing (spec 5.12).
   *
   * Neither `offers` nor `momentumCounts` is read from storage HERE. Both arrive
   * as props from App: the sidebar re-renders constantly, and a read on every
   * render was the original bug behind the offers prop — once offers moved to
   * Supabase that read became async, which a render cannot await.
   */
  const builtTools = builtToolIds({ offers, counts: momentumCounts });

  /* "Soon" tools can't be built, so they stay out of the denominator — otherwise
     Grow would count two tools that cannot be built yet. */
  /* Live FOR THIS PERSON — an admin-only tool counts only for the admin. */
  const adminViewer = isAdminEmail(user?.primaryEmailAddress?.emailAddress);
  const groupLive = (bk) => bk.items.filter((it) => toolStatus(it, adminViewer) === "live");
  const groupBuilt = (bk) => groupLive(bk).filter((it) => builtTools.has(it.id)).length;

  const momentumLiveTotal = BUCKETS.reduce((n, bk) => n + groupLive(bk).length, 0);
  const momentumDoneCount = BUCKETS.reduce((n, bk) => n + groupBuilt(bk), 0);

  const PHASE_PROGRESS = {
    ideation:    { done: ideationDoneCount,    total: IDEATION_MODULES.length },
    accelerator: { done: acceleratorDoneCount, total: ACCELERATOR_MODULES.length },
    momentum:    { done: momentumDoneCount,    total: momentumLiveTotal },
  };

  const [phase, setPhase] = useState(() => phaseOf(active) || "accelerator");
  /* An accordion, not a set of independent toggles: at most one module is open at
     a time, so working down the tree doesn't leave a trail of expanded sections to
     scroll past. Same for the Momentum buckets. Nothing starts expanded — landing
     on the app highlights where you are without unfolding it. */
  const [expandedModules, setExpandedModules] = useState({});
  /* ME, and the Assessment row inside it. Two levels, her call 2026-09-17:
     "Me will now have standard dropdown — 1) Assessment with subitems just
     like the whole accelerator, 2) Skills you can grey that out and put soon
     for now." So Me opens to its tools, and Assessment opens to its own rows,
     exactly as an Accelerator module opens to its locked sections. */
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [expandedBuckets, setExpandedBuckets] = useState({});
  const [hoveredPrompt, setHoveredPrompt] = useState(null);

  /** Open one and close whatever else was open; clicking the open one closes it. */
  const toggleModule = (id) => setExpandedModules((prev) => (prev[id] ? {} : { [id]: true }));
  const toggleBucket = (key) => setExpandedBuckets((prev) => (prev[key] ? {} : { [key]: true }));

  /* THE BUCKET HOLDING THE OPEN TOOL UNFOLDS. Her call, 2026-09-18, reversing
     her own earlier one: "I may have previously said I want the drawer closed
     in momentum but I changed my mind — just one at a time. So Build opens
     when you have offers open and when you go back to the nav it stays open
     and has offers selected."

     WHAT THAT REPLACES, kept because it was right about something: arriving
     somewhere used to unfold it, and that was removed because a phase switch
     or a restored session would bury the rest of the tree behind a section
     nobody asked to see. Her change is narrower than what was removed — it is
     the MOMENTUM BUCKET holding the tool she is actually in, one at a time,
     and nothing about Accelerator modules changed.

     It only fires when `active` changes, so it never fights her: open Build,
     tap Grow, and Grow stays open because nothing navigated. */
  useEffect(() => {
    const bucket = BUCKETS.find((b) => b.items.some((i) => toolNavId(i) === active));
    if (bucket) setExpandedBuckets({ [bucket.key]: true });
  }, [active]);

  /* Assessment unfolds inside Me only while she is on one of its two rows. */
  useEffect(() => {
    setAssessmentOpen(active === "archetype_results" || active === "archetype_feedback");
  }, [active]);

  /* Navigating from outside the sidebar (a transition CTA, a restored session)
     has to retarget the switcher, or the tree would hide the active item. Global
     destinations return null and leave the tree where it was. */
  useEffect(() => {
    const p = phaseOf(active);
    if (p) setPhase(p);
  }, [active]);

  /* THE BOTTOM BAR ASKING FOR A TAB, without going anywhere.
     Her report, 2026-09-18: the three phase buttons did three different things
     — Academy jumped to the Niche Decoder, Accelerator opened the sheet and
     selected nothing, Momentum jumped to Courses. All three now open the nav
     and select the tab, and nothing navigates.

     It is a REQUEST OBJECT, not a phase string, so tapping the same button
     twice works: a fresh object each time is a fresh identity, where an
     unchanged "momentum" would not re-fire this. Selecting a tab is all it
     does — `switchPhase` also moves the canvas to the phase's first item, and
     that is the jumping-to-a-page she is reporting.

     ABOVE THE NULL RETURN, with the other one, and it has to stay there. */
  useEffect(() => {
    if (phaseRequest?.phase) { setPhase(phaseRequest.phase); setMenuLevel("main"); }
  }, [phaseRequest]);

  /* THE SECOND-LEVEL MENUS — shape A, her call 2026-10-21. Ask Brill › and
     Me › each swap this menu for their OWN cream blush level with "← Main
     menu" on top, on a desktop exactly as on a phone; the screen on the right
     stays where it was. › always means "drills in". (Ask Brill's chats popped
     out beside the menu on a desktop for a day, 2026-10-20 — "different than
     everything else we do".) A Home row for the dashboard joins these when the
     dashboard is built; it is a screen, not a level.

     The level FOLLOWS WHERE SHE IS, the way Me used to fold away when she left
     it (her ask, 2026-10-15): a chat shows the chats, a Me screen shows Me,
     anywhere else the main menu. It only fires when `active` changes, so "←
     Main menu" stands until she next goes somewhere. The chats level needs
     0042 — before that, Ask Brill is a plain row. ABOVE THE NULL RETURN. */
  const askMenuOn = !!askChats?.available;
  const [menuLevel, setMenuLevel] = useState(() => menuLevelFor(active, askMenuOn));
  useEffect(() => {
    setMenuLevel(menuLevelFor(active, askMenuOn));
  }, [active, askMenuOn]);
  /* On a phone the menu is a sheet: it opens on the level she is in. */
  useEffect(() => {
    if (isMobile && isOpen) setMenuLevel(menuLevelFor(active, askMenuOn));
  }, [isOpen, isMobile, active, askMenuOn]);
  const askLevel = askMenuOn && menuLevel === "ask";
  const meLevel = menuLevel === "me";

  if (isMobile && !isOpen) return null;

  /* Destinations with no module transcript to reset. */
  const resetDisabled = ["chat", "settings", "admin", "social_generator", "niche", "niche_review"].includes(active);

  const selectPhase = (key) => {
    setPhase(key);
    /* Move the canvas to the phase's first item so the switcher and the canvas
       agree — but only highlight it. Unfolding it here buried the rest of the
       tree behind a section she hadn't asked to see.

       Skipped when the phase is locked: the tree shows its upsell instead, and
       selecting into a locked module would just bounce her to a gate. */
    if (key === "ideation") onSelect(IDEATION_MODULES[0]);
    else if (key === "accelerator" && hasAcceleratorAccess) onSelect(ACCELERATOR_MODULES[0]);
    else if (key === "momentum" && hasMomentumAccess) onSelect(toolNavId(BUCKETS[0].items[0]));
    setExpandedModules({});
    setExpandedBuckets({});
    /* Deliberately no onClose() on mobile. Tapping a phase means "show me this
       tree", so the sheet stays up. Getting *into* a section is the word tap. */
  };

  /* The three actions on a completed sub-step. Identical in both trees. */
  function hoverActions(id, promptName, output) {
    return (
      <div style={{ display: "flex", gap: 3, marginLeft: 4 }}>
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(id); }}
          title="Go to conversation"
          style={HOVER_ACTION}
        >
          <MessageCircle size={10} color="var(--chrome-accent)" />
        </button>
        {output && (
          <button
            onClick={(e) => { e.stopPropagation(); onViewOutput(id, promptName, output); }}
            title="View locked output"
            style={HOVER_ACTION}
          >
            <BookOpen size={10} color="var(--chrome-accent)" />
          </button>
        )}
        {output && onReviseSection && (
          <button
            onClick={(e) => { e.stopPropagation(); onReviseSection(id, promptName); }}
            title="Revise this section"
            style={HOVER_ACTION}
          >
            <Pencil size={10} color="var(--chrome-accent)" />
          </button>
        )}
      </div>
    );
  }

  function renderNavItem(id) {
    const meta = MODULE_META[id];
    const Icon = meta.icon;
    const isActive = active === id;
    const msgs = moduleMessages[id] || [];
    const completed = completedIn(id);
    const outputs = outputsIn(id);
    const visiblePrompts = getVisiblePrompts(id, msgs);
    const isExpanded = expandedModules[id];
    const hasBlueprint = !!blueprints[id];
    /* Sections here that were locked under a niche she has since left, and how
       badly. Salmon means the module was built around the old client and gets
       rebuilt; amber means it's only niche-flavoured and wants a read-through.
       These used to be one colour on the grounds that the chrome only has to say
       "something here" — but five identical salmon dots can't tell a rebuild from
       a read-through, which is the most useful thing to know at a glance. */
    const staleHere = nicheStaleByModule[id] || {};
    const staleCount = Object.keys(staleHere).length;
    const redoHere = Object.values(staleHere).filter((t) => t === "redo").length;
    const staleColor = nicheStaleTierByModule[id] === "check"
      ? "var(--brand-amber)"
      : "var(--brand-salmon)";

    return (
      <div key={id} style={{ marginBottom: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <button
            onClick={() => {
              onSelect(id);
              /* The word means "take me there". On mobile that has to dismiss the
                 sheet, or she navigates and stares at the sidebar still covering
                 it. Opening the tree without leaving is the arrow's job — it's a
                 separate tap target on mobile (below) and stops propagation. */
              if (isMobile) onClose();
              else if (visiblePrompts.length > 0) toggleModule(id);
            }}
            style={{
              flex: 1, display: "flex", alignItems: "center", gap: 8,
              padding: isMobile ? "12px 10px" : "9px 10px", borderRadius: 8, border: "none", cursor: "pointer",
              textAlign: "left", transition: "all 0.15s",
              background: isActive ? "var(--chrome-accent-solid)" : "transparent",
              color: isActive ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
              fontWeight: isActive ? 600 : 400, fontSize: isMobile ? 14 : 13,
            }}
          >
            <Icon size={isMobile ? 16 : 14} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
              <span>{meta.label}</span>
              {meta.note && <span style={{ fontSize: 11, opacity: 0.7, fontStyle: "italic", fontWeight: 400, lineHeight: 1.3 }}>{meta.note}</span>}
            </div>
            {/* Salmon or amber, never teal: teal means done and confirmed, and work
                a niche she has left is neither. Sits before the tick so it reads
                first — a module can be complete *and* need another look. */}
            {staleCount > 0 && (
              /* A span, not a button: this sits inside the module button and
                 nesting one button in another is invalid. stopPropagation keeps a
                 click here from also selecting the module underneath it. */
              <span
                role="button"
                tabIndex={0}
                title="What your niche change touched here"
                onClick={(e) => { e.stopPropagation(); onOpenNicheTriage?.(id); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onOpenNicheTriage?.(id); }
                }}
                style={{ flexShrink: 0, display: "flex", cursor: "pointer", padding: 2, margin: -2 }}
              >
                <AlertCircle
                  size={12}
                  strokeWidth={2.5}
                  color={isActive ? "var(--chrome-on-accent-solid)" : staleColor}
                />
              </span>
            )}
            {visiblePrompts.length > 0 && completed.size === visiblePrompts.length && (
              <CheckCircle size={13} color={isActive ? "var(--chrome-on-accent-solid)" : "var(--chrome-accent)"} style={{ flexShrink: 0 }} />
            )}
            {hasBlueprint && <Scroll size={11} color="var(--chrome-accent)" style={{ flexShrink: 0 }} />}
            {!isMobile && visiblePrompts.length > 0 && (isExpanded
              ? <ChevronDown size={11} style={{ flexShrink: 0, opacity: 0.5 }} />
              : <ChevronRight size={11} style={{ flexShrink: 0, opacity: 0.5 }} />
            )}
          </button>
          {isMobile && visiblePrompts.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleModule(id);
              }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: isActive ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                padding: "12px 10px",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, minWidth: 44, minHeight: 44,
              }}
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
          )}
        </div>

        {visiblePrompts.length > 0 && isExpanded && (
          <div style={{ marginLeft: 8, marginTop: 2, marginBottom: 4 }}>
            <ProgressBar completed={completed.size} total={visiblePrompts.length} />
            {/* The note the flag needs. A bare "!" is ambiguous, and a tooltip
                doesn't exist on mobile — so say it in words, once, where she's
                already looking at the affected sections. */}
            {staleCount > 0 && (
              /* A button, not a div — this is the wide, obvious way into the
                 triage screen. The "!" on the module row above opens the same
                 thing, but it's a 12px target and she may not think to press it. */
              <button
                onClick={() => onOpenNicheTriage?.(id)}
                style={{
                  display: "flex", gap: 5, alignItems: "flex-start", textAlign: "left",
                  width: "100%", margin: "5px 0 6px", padding: "6px 8px", borderRadius: 6,
                  background: "var(--chrome-hover)", border: "none",
                  borderLeft: `2px solid ${staleColor}`,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <AlertCircle size={10} strokeWidth={2.5} color={staleColor} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 10, lineHeight: 1.5, color: "var(--chrome-muted)" }}>
                  Your niche changed — {redoHere === 0
                    ? "worth a read-through, probably still fine."
                    : redoHere === staleCount
                      ? "we rebuild this one together."
                      : `${redoHere} of these ${redoHere === 1 ? "needs" : "need"} rebuilding, the rest just a read.`}
                  {" "}Tap to sort it out.
                </span>
              </button>
            )}
            {visiblePrompts.map((p, i) => {
              const done = completed.has(p);
              const hasOutput = !!outputs[p];
              const isHovered = hoveredPrompt === `${id}-${i}`;
              return (
                <div
                  key={i}
                  onMouseEnter={() => setHoveredPrompt(`${id}-${i}`)}
                  onMouseLeave={() => setHoveredPrompt(null)}
                  style={{
                    display: "flex", alignItems: "center",
                    padding: isMobile ? "9px 6px 9px 8px" : "5px 6px 5px 8px",
                    borderLeft: `2px solid ${done ? "var(--chrome-accent)" : "var(--chrome-border)"}`,
                    marginBottom: 1, borderRadius: "0 6px 6px 0",
                    background: isHovered ? "var(--chrome-hover)" : "transparent",
                    transition: "background 0.1s",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (done) {
                      if (hasOutput) onViewOutput(id, p, outputs[p]);
                      else onSelect(id);
                    } else {
                      onStartPrompt(id, p);
                    }
                    if (isMobile) onClose();
                  }}
                >
                  {done
                    ? <CheckCircle size={isMobile ? 13 : 11} color="var(--chrome-accent)" style={{ flexShrink: 0 }} />
                    : <Circle size={isMobile ? 13 : 11} color="var(--chrome-disabled)" style={{ flexShrink: 0 }} />
                  }
                  <span style={{ flex: 1, fontSize: isMobile ? 13 : 11, marginLeft: 6, color: done ? "var(--chrome-text-secondary)" : "var(--chrome-muted)", fontWeight: done ? 500 : 400 }}>
                    {p}
                  </span>
                  {/* Which specific sections the note above is talking about, and
                      in their own colour — a module is usually mixed, so the row's
                      dot and the module's dot won't always agree. */}
                  {staleHere[p] && !(done && isHovered && !isMobile) && (
                    <AlertCircle
                      size={10}
                      strokeWidth={2.5}
                      color={staleHere[p] === "check" ? "var(--brand-amber)" : "var(--brand-salmon)"}
                      style={{ flexShrink: 0, marginRight: 2 }}
                    />
                  )}
                  {done && isHovered && !isMobile && hoverActions(id, p, outputs[p])}
                </div>
              );
            })}
            {(() => {
              const done = completed.size === visiblePrompts.length;
              return (
                <>
                  <button
                    onClick={done ? (id === "offer_ideation" ? onViewIdeationBlueprint : () => onViewBlueprint(id)) : undefined}
                    disabled={!done}
                    style={{
                      width: "100%", marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                      padding: "6px 8px", borderRadius: 6,
                      background: done ? "var(--chrome-accent-quiet)" : "var(--chrome-quiet)",
                      border: `1px solid ${done ? "var(--chrome-accent-line)" : "var(--chrome-border)"}`,
                      cursor: done ? "pointer" : "default",
                      color: done ? "var(--chrome-accent)" : "var(--chrome-disabled)",
                      fontSize: 11, fontWeight: 600, transition: "all 0.15s",
                    }}
                  >
                    <Scroll size={10} />
                    {BLUEPRINT_LABELS[id] || "View Full Blueprint"}
                  </button>
                  {!done && (
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--chrome-muted)", textAlign: "center", lineHeight: 1.4 }}>
                      A formatted summary of everything you built in this module
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  }

  /* Ideation's single-prompt modules are one row with no sub-tree, so they don't
     go through renderNavItem — clicking one opens its locked output directly. */
  function renderIdeationLeaf(id) {
    const meta = MODULE_META[id];
    const Icon = meta.icon;
    const completed = completedIn(id);
    const outputs = outputsIn(id);
    const promptName = MODULE_PROMPTS[id]?.[0];
    const done = promptName ? completed.has(promptName) : false;
    const output = done && promptName ? outputs[promptName] : undefined;
    const isActive = active === id;
    const isHovered = hoveredPrompt === `ideation-${id}`;
    return (
      <div
        key={id}
        onMouseEnter={() => setHoveredPrompt(`ideation-${id}`)}
        onMouseLeave={() => setHoveredPrompt(null)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: isMobile ? "12px 10px" : "9px 10px",
          borderRadius: 8, marginBottom: 2,
          background: isActive ? "var(--chrome-accent-solid)" : isHovered ? "var(--chrome-hover)" : "transparent",
          color: isActive ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
          fontWeight: isActive ? 600 : 400,
          fontSize: isMobile ? 14 : 13,
          cursor: "pointer", transition: "all 0.15s",
        }}
        onClick={() => {
          if (output) onViewOutput(id, promptName, output);
          else onSelect(id);
          if (isMobile) onClose();
        }}
      >
        <Icon size={isMobile ? 16 : 14} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{meta.label}</span>
        {done && <CheckCircle size={13} color={isActive ? "var(--chrome-on-accent-solid)" : "var(--chrome-accent)"} style={{ flexShrink: 0 }} />}
        {done && isHovered && !isMobile && hoverActions(id, promptName, output)}
      </div>
    );
  }

  /* The locked list + upsell shown when a phase hasn't been bought. */
  function renderLocked(ids, cta) {
    return (
      <div style={{ padding: "4px 0 8px" }}>
        {ids.map((id) => {
          const meta = MODULE_META[id];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <div key={id} style={LOCKED_ROW}>
              <Icon size={14} color="var(--chrome-disabled)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "var(--chrome-disabled)", flex: 1 }}>{meta.label}</span>
              <Lock size={10} color="var(--chrome-disabled)" style={{ flexShrink: 0 }} />
            </div>
          );
        })}
        {cta}
      </div>
    );
  }

  /**
   * Locked Momentum shows the real bucket structure, not the legacy flat module
   * list. Someone deciding whether to buy should see what they would actually
   * get — and the old list still advertised Content Studio / Operations Hub /
   * Sales Hub, names the restructure retired.
   *
   * Groups stay browsable so the whole shape is explorable; the tools inside
   * are inert. No progress bar: nothing can be built yet, so "0/3 built" would
   * read as failure rather than as an invitation.
   */
  function renderLockedMomentum(cta) {
    return (
      <div>
        {BUCKETS.map((bk) => {
          const open = !!expandedBuckets[bk.key];
          return (
            <div key={bk.key} style={GROUP_CARD}>
              <div
                onClick={() => toggleBucket(bk.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  minHeight: 44, padding: "10px 12px", cursor: "pointer", userSelect: "none",
                  background: open ? "var(--chrome-hover)" : "transparent",
                  transition: "background 0.2s cubic-bezier(0.4,0,0.2,1)",
                }}
                onMouseEnter={e => { if (!open) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--chrome-text-secondary)", lineHeight: 1.2 }}>{bk.label}</span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "var(--chrome-muted)" }}>
                    {bk.items.length} tools
                  </span>
                </div>
                <Lock size={11} color="var(--chrome-muted)" style={{ flexShrink: 0 }} />
                {open
                  ? <ChevronDown size={14} color="var(--chrome-muted)" style={{ flexShrink: 0 }} />
                  : <ChevronRight size={14} color="var(--chrome-muted)" style={{ flexShrink: 0 }} />
                }
              </div>
              {open && (
                <div style={{ padding: "2px 8px 8px 10px" }}>
                  {bk.items.map((it) => {
                    const Icon = it.icon;
                    return (
                      <div key={it.id} style={LOCKED_ROW}>
                        <Icon size={14} color="var(--chrome-disabled)" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: "var(--chrome-disabled)", flex: 1 }}>{it.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {cta}
      </div>
    );
  }

  /* THE COUNT GOES SALMON WHILE SOMETHING NEEDS ADDRESSING.
     Now that a section built under a niche she has left still counts as done,
     the count alone would read as finished when part of it was written for a
     client she no longer serves. Her call, 2026-09-01: keep the ticks and the
     total, and "turn the 9/9 salmon so it shows that it needs to be addressed
     until she either says they are good or redoes them."

     Driven by `nicheStaleCount` — the SAME prop behind the module dots and the
     row-level "!" — so the count and the marks beside it can never say
     different things. It clears the moment she confirms a section as still true
     or rebuilds it, because that is what clears the stamp underneath.

     ASK IT PER PHASE, NOT "IS THIS MOMENTUM". The first version of this read
     `phase !== "momentum" && nicheStaleCount > 0`, which treated "not Momentum"
     as "can go stale". `NICHE_SENSITIVITY` maps ACCELERATOR modules only —
     neither Ideation module is in it — so a stale My Mission turned the Ideation
     count salmon too, flagging a phase where nothing was wrong and nothing could
     be acted on. Shipped in `5e955a9` and caught within the hour by her asking
     how many phases there are.

     `nicheStaleByModule` is keyed by module, and `phaseOf` already knows which
     phase owns a module, so this asks the exact question: is any of the work
     flagged as stale IN THE PHASE SHE IS LOOKING AT. It stays correct on its own
     if Ideation or Momentum ever gains a niche-sensitive module — nothing here
     names a phase. */
  const phaseNeedsReview = Object.keys(nicheStaleByModule)
    .some((moduleKey) => phaseOf(moduleKey) === phase);
  const phaseTone = phaseNeedsReview
    ? "var(--brand-salmon)"
    : PHASES.find((p) => p.key === phase)?.tone;
  const { done: phaseDone, total: phaseTotal } = PHASE_PROGRESS[phase];

  return (
    <>
      {isMobile && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }} />}
      <div style={{
        width: SIDEBAR_WIDTH, background: "var(--chrome-bg)", display: "flex", flexDirection: "column",
        height: isMobile ? "100dvh" : "100vh", flexShrink: 0,
        position: isMobile ? "fixed" : "relative",
        left: 0, top: 0, zIndex: isMobile ? 50 : "auto",
        boxShadow: isMobile ? "4px 0 20px rgba(0,0,0,0.3)" : "none",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        paddingLeft: "env(safe-area-inset-left)",
        /* The sidebar is dark in BOTH panel modes, so its scrollbar is always
           the lightening tint, never the light canvas's darkening one. */
        "--scroll-thumb": "rgba(255,255,255,0.16)",
        "--scroll-thumb-hover": "rgba(255,255,255,0.30)",
        "--scroll-thumb-stroke": "rgba(255,255,255,0.42)",
      }}>
        {/* ── Brand header ───────────────────────────────────────────────── */}
        <div style={{ padding: "18px 14px 14px", borderBottom: "1px solid var(--chrome-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          {/* The lockup already says "brill by Tevra Wood" — the typed name
              and "Tevra Wood Consulting" under it would say it twice. 42px
              tall keeps it past the lockup's 120px minimum width. */}
          <BrillLogo height={42} />

          {isMobile && (
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--chrome-text-secondary)", padding: 4 }}>
              <X size={17} />
            </button>
          )}
        </div>

        {askLevel ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", ...MENU2 }}>
            <AskBrillMenu
              chats={askChats.chats}
              hasEarlier={askChats.hasEarlier}
              activeKey={askChats.activeKey}
              isMobile={isMobile}
              onBack={() => setMenuLevel("main")}
              onNew={onNewAskChat}
              onOpen={onOpenAskChat}
              onRename={onRenameAskChat}
              onDelete={onDeleteAskChat}
            />
          </div>
        ) : meLevel ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, padding: "10px 8px 12px", ...MENU2_ME }}>
            <Menu2Header onBack={() => setMenuLevel("main")} icon={CircleUserRound} title="Me" />
            {/* ASSESSMENT — a tool with rows under it, like a module. The
                row itself only opens; the assessment is taken on her site. */}
            <div
              onClick={() => setAssessmentOpen((v) => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: isMobile ? "11px 9px" : "8px 9px",
                borderRadius: 8, cursor: "pointer",
                color: "var(--chrome-text-secondary)",
                fontSize: isMobile ? 13.5 : 12.5,
                marginBottom: 1,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--chrome-hover)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <Sparkles size={isMobile ? 15 : 13} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>Assessment</span>
              {assessmentOpen
                ? <ChevronDown size={10} style={{ flexShrink: 0, opacity: 0.5 }} />
                : <ChevronRight size={10} style={{ flexShrink: 0, opacity: 0.5 }} />
              }
            </div>

            {assessmentOpen && (
              <div style={{ marginLeft: 8 }}>
                <a
                  href="https://info.tevrawood.com/archetype-assessment"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: isMobile ? "10px 8px 10px 10px" : "7px 8px 7px 10px",
                    borderLeft: "2px solid var(--chrome-accent)",
                    borderRadius: "0 6px 6px 0",
                    textDecoration: "none",
                    color: "var(--chrome-text-secondary)",
                    fontSize: isMobile ? 13 : 12,
                    marginBottom: 1,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--chrome-hover)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <ExternalLink size={isMobile ? 14 : 12} style={{ flexShrink: 0 }} />
                  <span>Take the Assessment</span>
                </a>
                {[
                  { id: "archetype_results",  icon: Award,         label: "Archetype Results"  },
                  { id: "archetype_feedback", icon: MessageCircle, label: "Archetype Feedback" },
                ].map(({ id, icon: Icon, label }) => {
                  const on = active === id;
                  return (
                    <div
                      key={id}
                      onClick={() => { onSelect(id); if (isMobile) onClose(); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: isMobile ? "10px 8px 10px 10px" : "7px 8px 7px 10px",
                        borderLeft: "2px solid var(--chrome-accent)",
                        borderRadius: "0 6px 6px 0",
                        cursor: "pointer",
                        color: on ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                        background: on ? "var(--chrome-accent-solid)" : "transparent",
                        fontWeight: on ? 600 : 400,
                        fontSize: isMobile ? 13 : 12,
                        marginBottom: 1,
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                      onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                    >
                      <Icon size={isMobile ? 14 : 12} style={{ flexShrink: 0 }} />
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* BUSINESS — the basics (2026-10-13): name, website, what kind of business. The import-and-audit under it is stored, not built (docs/me-business-import-spec.md). */}
            {(() => {
              const on = active === "me_business";
              return (
                <div
                  onClick={() => { onSelect("me_business"); if (isMobile) onClose(); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: isMobile ? "11px 9px" : "8px 9px",
                    borderRadius: 8, cursor: "pointer", marginBottom: 1,
                    fontSize: isMobile ? 13.5 : 12.5,
                    color: on ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                    background: on ? "var(--chrome-accent-solid)" : "transparent",
                    fontWeight: on ? 600 : 400,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                >
                  <Building2 size={isMobile ? 15 : 13} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Business</span>
                </div>
              );
            })()}

            {/* VOICE (was Writing until later the same day) — her own writing rules and, per tool, where one sounds different (voice-layers spec, layers 6 and 7,
                2026-09-24). A screen, not a tool with rows, so it opens on
                the press like the Assessment's screen rows do. */}
            {(() => {
              const on = active === "me_voice" || active === "me_writing";
              return (
                <div
                  onClick={() => { onSelect("me_voice"); if (isMobile) onClose(); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: isMobile ? "11px 9px" : "8px 9px",
                    borderRadius: 8, cursor: "pointer", marginBottom: 1,
                    fontSize: isMobile ? 13.5 : 12.5,
                    color: on ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                    background: on ? "var(--chrome-accent-solid)" : "transparent",
                    fontWeight: on ? 600 : 400,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                >
                  <PenLine size={isMobile ? 15 : 13} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Voice</span>
                </div>
              );
            })()}

            {/* SKILLS — built 2026-09-25 as a selector (her call: "only that
                for now"). It was greyed "Soon" at the top of Me, which read as
                unfinished right where every coach starts. Opens like Voice. */}
            {(() => {
              const on = active === "me_skills";
              return (
                <div
                  onClick={() => { onSelect("me_skills"); if (isMobile) onClose(); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: isMobile ? "11px 9px" : "8px 9px",
                    borderRadius: 8, cursor: "pointer", marginBottom: 1,
                    fontSize: isMobile ? 13.5 : 12.5,
                    color: on ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                    background: on ? "var(--chrome-accent-solid)" : "transparent",
                    fontWeight: on ? 600 : 400,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                >
                  <Award size={isMobile ? 15 : 13} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Skills</span>
                </div>
              );
            })()}

            {/* BIO — her career and credentials, the background her Story does not cover (2026-10-13). Not the LinkedIn Bio; My Positioning writes that. */}
            {(() => {
              const on = active === "me_bio";
              return (
                <div
                  onClick={() => { onSelect("me_bio"); if (isMobile) onClose(); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: isMobile ? "11px 9px" : "8px 9px",
                    borderRadius: 8, cursor: "pointer", marginBottom: 1,
                    fontSize: isMobile ? 13.5 : 12.5,
                    color: on ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                    background: on ? "var(--chrome-accent-solid)" : "transparent",
                    fontWeight: on ? 600 : 400,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                >
                  <BookUser size={isMobile ? 15 : 13} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Bio</span>
                </div>
              );
            })()}

            {/* BRAND — her business name, logo and colours as they are today
                (2026-10-12). Every tool wears these; "Me wins" over the
                Accelerator's palette until she says otherwise. */}
            {(() => {
              const on = active === "me_brand";
              return (
                <div
                  onClick={() => { onSelect("me_brand"); if (isMobile) onClose(); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: isMobile ? "11px 9px" : "8px 9px",
                    borderRadius: 8, cursor: "pointer", marginBottom: 1,
                    fontSize: isMobile ? 13.5 : 12.5,
                    color: on ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                    background: on ? "var(--chrome-accent-solid)" : "transparent",
                    fontWeight: on ? 600 : 400,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                >
                  <Palette size={isMobile ? 15 : 13} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Brand</span>
                </div>
              );
            })()}
          </div>
        ) : (<>
        {/* ── Global destinations — belong to no phase ────────────────────── */}
        <div style={{ padding: "10px 8px 8px", borderBottom: "1px solid var(--chrome-border)", flexShrink: 0 }}>
          {askMenuOn ? (
            <button
              onClick={() => { onSelect("chat"); setMenuLevel("ask"); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8, border: "none", borderRadius: 8, marginBottom: 2,
                background: active === "chat" ? "var(--chrome-accent-solid)" : "transparent",
                color: active === "chat" ? "var(--chrome-on-accent-solid)" : "var(--chrome-text-secondary)",
                cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                padding: isMobile ? "12px 10px" : "9px 10px",
                fontSize: isMobile ? 14 : 13, fontWeight: active === "chat" ? 600 : 400,
              }}
            >
              <MessageSquare size={14} />
              <span style={{ flex: 1 }}>Ask Brill</span>
              <ChevronRight size={12} style={{ opacity: 0.6 }} />
            </button>
          ) : renderNavItem("chat")}
          <div
            onClick={() => setMenuLevel("me")}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: isMobile ? "12px 10px" : "9px 10px",
              borderRadius: 8, cursor: "pointer",
              color: "var(--chrome-text-secondary)",
              fontSize: isMobile ? 14 : 13,
              fontWeight: 400, marginBottom: 2,
              transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--chrome-hover)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <CircleUserRound size={isMobile ? 16 : 14} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>Me</span>
            <ChevronRight size={12} style={{ flexShrink: 0, opacity: 0.6 }} />
          </div>
        </div>

        {/* ── Phase switcher ─────────────────────────────────────────────── */}
        <div style={{ padding: "12px 10px 10px", borderBottom: "1px solid var(--chrome-border)", flexShrink: 0 }}>
          <div style={{
            display: "flex", gap: 1, padding: 2, borderRadius: 8,
            background: "var(--chrome-quiet)", border: "1px solid var(--chrome-border)",
          }}>
            {PHASES.map((p) => {
              const on = phase === p.key;
              const locked = (p.key === "accelerator" && !hasAcceleratorAccess)
                          || (p.key === "momentum" && !hasMomentumAccess);
              return (
                <button
                  key={p.key}
                  onClick={() => selectPhase(p.key)}
                  aria-pressed={on}
                  // The lock is decorative; the state has to reach a screen
                  // reader through the label, not the icon.
                  aria-label={locked ? `${p.label} — locked` : p.label}
                  style={{
                    flex: 1, borderRadius: 6, border: "none", cursor: "pointer",
                    padding: "7px 1px", fontSize: 10, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.01em",
                    fontFamily: "inherit", transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                    background: on ? p.tone : "transparent",
                    color: on ? "var(--chrome-on-accent)" : "var(--chrome-muted)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  {locked && <Lock size={9} strokeWidth={2.5} style={{ flexShrink: 0 }} />}
                  {p.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 9, marginBottom: 5 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: "var(--chrome-text-secondary)" }}>
              {PHASES.find((p) => p.key === phase)?.label}
            </span>
            {/* Ideation and the Accelerator have a real denominator — a module is
                complete or it isn't. Momentum doesn't: a tool holding one saved
                record isn't a fraction of done, so the count is left off and the
                bar below becomes the four bucket colours instead. */}
            {phase !== "momentum" && (
              <span style={{ fontSize: 11, fontWeight: 600, color: phaseTone }}>
                {phaseDone}/{phaseTotal}
              </span>
            )}
          </div>
          {phase === "momentum" ? (
            <div style={{ height: 3, borderRadius: 4, background: PHASE_RAMP, opacity: 0.85 }} />
          ) : (
            <div style={{ height: 3, borderRadius: 4, background: "var(--chrome-quiet)", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 4, background: phaseTone,
                width: phaseTotal ? `${(phaseDone / phaseTotal) * 100}%` : "0%",
                transition: "width 0.35s cubic-bezier(0.4,0,0.2,1)",
              }} />
            </div>
          )}

          {/* Her niche lives here rather than in the tree below: it belongs to
              the phase, not to the sequence of modules, and it has no sub-steps
              or completion state to show. Tucked under the progress bar and
              right-aligned so it's findable without competing with anything —
              the tree is where the work is.

              Absent until a niche exists; before that the app is still leading
              her to build one in My Mission. Hidden under Ideation, which has
              its own Niche Decoder. */}
          {activeNiche && onOpenNiche && hasAcceleratorAccess && phase !== "ideation" && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <button
                onClick={onOpenNiche}
                title={nicheStaleCount > 0
                  ? `${nicheStaleCount} ${nicheStaleCount === 1 ? "module holds work" : "modules hold work"} built on an earlier niche`
                  : "Your locked niche, and every niche you've held"}
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: 0, background: "none", border: "none",
                  cursor: "pointer", fontFamily: "inherit",
                  fontSize: 9.5, fontWeight: 600, letterSpacing: "0.05em",
                  textTransform: "uppercase", lineHeight: 1.4,
                  color: "var(--chrome-muted)",
                  opacity: active === "niche" || active === "niche_review" ? 1 : 0.75,
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => {
                  if (active !== "niche" && active !== "niche_review") e.currentTarget.style.opacity = 0.75;
                }}
              >
                {/* The one spot of colour — enough to find, not enough to shout.
                    The word stays grey so the whole thing still reads as a label
                    in the phase header rather than a call to action. */}
                <RotateCcw
                  size={9}
                  strokeWidth={2.5}
                  color="var(--chrome-accent)"
                  style={{ flexShrink: 0 }}
                />
                Niche
              </button>
            </div>
          )}
        </div>

        {/* ── The selected phase's tree — only one is ever mounted ────────── */}
        <nav style={{ flex: 1, padding: "10px 8px 16px", overflowY: "auto" }}>
          {phase === "ideation" && (
            <>
              {IDEATION_MODULES.filter((id) => (MODULE_PROMPTS[id] || []).length === 1).map(renderIdeationLeaf)}
              {IDEATION_MODULES.filter((id) => (MODULE_PROMPTS[id] || []).length > 1).map((id) => renderNavItem(id))}
            </>
          )}

          {phase === "accelerator" && (
            hasAcceleratorAccess ? (
              <>
                {ACCELERATOR_MODULES.map((id) => renderNavItem(id))}
                {allAcceleratorComplete && (
                  <button
                    onClick={onViewMasterBlueprint}
                    style={{
                      width: "100%", marginTop: 6, marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "8px 10px", borderRadius: 8,
                      background: "var(--chrome-accent-quiet)",
                      border: "1px solid var(--chrome-accent-line)",
                      cursor: "pointer", color: "var(--chrome-accent)", fontSize: 11, fontWeight: 700,
                      letterSpacing: "0.03em", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "var(--chrome-accent)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "var(--chrome-accent-line)"}
                  >
                    <Scroll size={11} />
                    Build Master Blueprint
                  </button>
                )}
              </>
            ) : renderLocked(ACCELERATOR_MODULES, (
              <a
                href="https://info.tevrawood.com/business-brilliance-accelerator"
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "8px 4px 0", padding: "8px", borderRadius: 8,
                  background: "var(--chrome-accent-quiet)", border: "1px solid var(--chrome-accent-line)",
                  color: "var(--chrome-accent)", fontSize: 11, fontWeight: 600,
                  textDecoration: "none", gap: 5,
                }}
              >
                <Lock size={10} />
                Enroll to unlock
              </a>
            ))
          )}

          {phase === "momentum" && (
            hasMomentumAccess ? (
              BUCKETS.map((bk) => {
                const open = !!expandedBuckets[bk.key];
                return (
                  <div key={bk.key} style={GROUP_CARD}>
                    <div
                      onClick={() => toggleBucket(bk.key)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        minHeight: 44, padding: "10px 12px", cursor: "pointer", userSelect: "none",
                        background: open ? "var(--chrome-hover)" : "transparent",
                        transition: "background 0.2s cubic-bezier(0.4,0,0.2,1)",
                      }}
                      onMouseEnter={e => { if (!open) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                      onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
                    >
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--chrome-text)", lineHeight: 1.2 }}>{bk.label}</span>
                        {/* A full line in the bucket's own colour, NOT a progress
                            bar. The fraction it used to show ("1/3 built") read as
                            completion, which nothing here can actually tell you —
                            an offer ladder with one row saved is not "1/3 done",
                            it is just started. So the line identifies the bucket
                            and the four of them read as a colour progression down
                            the tree.

                            It fades left-to-right into the colour rather than
                            sitting at full strength across: at four solid lines
                            the chrome shouted, and the ramp gives the eye a
                            direction without pretending to measure anything. */}
                        <div style={{ height: 4, borderRadius: 4, background: bucketLine(bk.color) }} />
                      </div>
                      {open
                        ? <ChevronDown size={14} color="var(--chrome-muted)" style={{ flexShrink: 0 }} />
                        : <ChevronRight size={14} color="var(--chrome-muted)" style={{ flexShrink: 0 }} />
                      }
                    </div>
                    {open && (
                      <div style={{ padding: "2px 8px 8px 10px" }}>
                        {bk.items.map((it) => {
                          const navId = toolNavId(it);
                          const on = active === navId;
                          const Icon = it.icon;
                          const soon = toolStatus(it, adminViewer) !== "live";
                          return (
                            <div
                              key={it.id}
                              onClick={() => { onSelect(navId); if (isMobile) onClose(); }}
                              style={{
                                display: "flex", alignItems: "center", gap: 10,
                                padding: 8, marginBottom: 1, borderRadius: 8, cursor: "pointer",
                                background: on ? "var(--chrome-accent-quiet)" : "transparent",
                                transition: "background 0.2s cubic-bezier(0.4,0,0.2,1)",
                              }}
                              onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--chrome-hover)"; }}
                              onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
                            >
                              <Icon
                                size={14}
                                color={soon ? "var(--chrome-disabled)" : on ? "var(--chrome-text)" : "var(--chrome-text-secondary)"}
                                style={{ flexShrink: 0 }}
                              />
                              <span style={{
                                flex: 1, fontSize: 13, lineHeight: 1.25,
                                color: soon ? "var(--chrome-disabled)" : on ? "var(--chrome-text)" : "var(--chrome-text-secondary)",
                                fontWeight: on ? 600 : 400,
                              }}>
                                {it.label}
                              </span>
                              {builtTools.has(it.id) && <CheckCircle size={14} color="var(--chrome-accent)" style={{ flexShrink: 0 }} />}
                              {soon && (
                                <span style={{
                                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                                  color: "var(--chrome-disabled)", border: "1px solid var(--chrome-border)",
                                  borderRadius: 999, padding: "2px 6px", flexShrink: 0,
                                }}>
                                  Soon
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : renderLockedMomentum((
              <div style={{
                margin: "8px 4px 0", padding: "8px 10px", borderRadius: 8,
                background: "var(--chrome-quiet)", border: "1px solid var(--chrome-border)",
                fontSize: 11, color: "var(--chrome-text-secondary)", lineHeight: 1.6, fontStyle: "italic",
              }}>
                Momentum is included with the full Accelerator — your toolkit for delivering, creating, and growing after the foundation is built.
              </div>
            ))
          )}
        </nav>

        </>)}

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        {/* Under a second-level menu it takes that level's colours too (her
            catch on the canvas: a dark footer under cream blush reads as a
            different panel). */}
        <div style={{ padding: "10px 14px", borderTop: "1px solid var(--chrome-border)", flexShrink: 0, ...(askLevel || meLevel ? MENU2 : {}) }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {[
              { icon: Globe,     label: "Portal",    action: () => window.open("https://portal.tevrawood.com/", "_blank") },
              { icon: Users,     label: "Community", action: () => window.open("https://portal.tevrawood.com/communities/groups/business-brilliance-collab/home", "_blank") },
              { icon: Settings,  label: "Settings",  action: () => { onSelect("settings"); if (isMobile) onClose(); } },
              ...(adminViewer
                ? [{ icon: BarChart2, label: "Admin", action: () => { onSelect("admin"); if (isMobile) onClose(); } }]
                : []),
            ].map(({ icon: Icon, label, action }) => (
              <button key={label} onClick={action} title={label} style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                background: "var(--chrome-quiet)", border: "1px solid var(--chrome-border)", borderRadius: 8,
                padding: "7px 4px", cursor: "pointer", color: "var(--chrome-text-secondary)",
                transition: "all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--chrome-hover)"; e.currentTarget.style.color = "var(--chrome-accent)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--chrome-quiet)"; e.currentTarget.style.color = "var(--chrome-text-secondary)"; }}
              >
                <Icon size={13} />
                <span style={{ fontSize: 10 }}>{label}</span>
              </button>
            ))}
          </div>
          {user && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "var(--chrome-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.primaryEmailAddress?.emailAddress}
              </div>
            </div>
          )}
          <button
            onClick={onSignOut}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              background: "none", border: "1px solid var(--chrome-border)", borderRadius: 6,
              padding: "6px 10px", cursor: "pointer", color: "var(--chrome-text-secondary)", fontSize: 11,
              marginBottom: 8,
            }}
          >
            <LogOut size={11} />
            Sign out
          </button>
          <button
            onClick={() => onResetModule(active)}
            disabled={resetDisabled}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              background: "none", borderRadius: 6,
              border: `1px solid ${resetDisabled ? "var(--chrome-border)" : "var(--chrome-danger-line)"}`,
              padding: "6px 10px", cursor: resetDisabled ? "not-allowed" : "pointer",
              color: resetDisabled ? "var(--chrome-disabled)" : "var(--chrome-text-secondary)", fontSize: 11,
            }}
          >
            <RotateCcw size={11} color={resetDisabled ? "var(--chrome-disabled)" : "var(--chrome-danger)"} />
            Reset this module
          </button>
        </div>
      </div>
    </>
  );
}
